import Card from "@/components/common/Card";
import {
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  CalendarIcon,
  EnvelopeIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { formatDate, getInitials } from "@/utils/formatters";
import { API_BASE_URL } from "@/lib/axios";

interface ProfileHeaderProps {
  user: any;
  companyData?: any;
  isCompanyAdmin: boolean;
}

const ProfileHeader = ({ user, companyData, isCompanyAdmin }: ProfileHeaderProps) => {
  const { t } = useTranslation();

  const getImageUrl = (imagePath: string) => {
    if (!imagePath) return "";
    if (imagePath.startsWith('data:')) {
      return imagePath;
    }
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    return `${imagePath}`;
  };

  // Get user initials safely
  const getUserInitials = () => {
    return getInitials(user?.fullName || "");
  };

  // Get profile image URL for regular users (same logic as navbar)
  const getProfileImageUrl = (): string | undefined => {
    if (user?.photo) {
      // If it's a data URL (base64), use it directly
      if (user.photo.startsWith("data:")) {
        return user.photo;
      }
      // If it's a filename, construct the API URL
      return `${user.photo}`;
    }
    return undefined;
  };

  return (
    <>
      <div className="mb-8 flex items-center justify-between">
        <div className="space-y-2">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-primary-600 to-gray-700 bg-clip-text text-transparent dark:from-white dark:via-primary-400 dark:to-gray-300"
          >
            {t("profile.title")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-600 dark:text-gray-400"
          >
            {t("profile.subtitle")}
          </motion.p>
        </div>
      </div>

      <div className={`grid gap-8 ${isCompanyAdmin ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
        {/* User Profile Card - Only show for non-company admins */}
        {!isCompanyAdmin && (
          <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
            <div className="flex flex-col items-center text-center p-6">
              <div className="relative mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="relative"
                >
                  {getProfileImageUrl() ? (
                    <img
                      src={getProfileImageUrl()}
                      alt={user.fullName || t("profile.user")}
                      className="h-32 w-32 rounded-full object-cover shadow-2xl ring-4 ring-primary-100 dark:ring-primary-900"
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        target.nextElementSibling?.classList.remove("hidden");
                      }}
                    />
                  ) : null}

                  {/* Fallback to initials */}
                  <div
                    className={`flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 dark:from-primary-900 dark:to-primary-800 dark:text-primary-300 shadow-2xl ring-4 ring-primary-100 dark:ring-primary-900 ${
                      getProfileImageUrl() ? "hidden" : ""
                    }`}
                  >
                    <span className="text-2xl font-bold">
                      {getUserInitials()}
                    </span>
                  </div>
                </motion.div>
              </div>

              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="text-xl font-bold text-gray-900 dark:text-white mb-2"
              >
                {user?.fullName || t("profile.unknownUser")}
              </motion.h3>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="text-gray-600 dark:text-gray-400 mb-4 text-sm"
              >
                {user?.email || t("profile.noEmail")}
              </motion.p>

              <div className="space-y-2 w-full">
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <IdentificationIcon className="h-4 w-4" />
                  <span>{user?.role || t("profile.unknownRole")}</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <EnvelopeIcon className="h-4 w-4" />
                  <span>{user?.phoneNumber || t("profile.noPhone")}</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <CalendarIcon className="h-4 w-4" />
                  <span>
                    {user?.createdAt
                      ? formatDate(user.createdAt)
                      : t("profile.unknownDate")}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Company Info Card - Only show for company admins */}
        {isCompanyAdmin && companyData && (
          <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
            <div className="flex flex-col items-center text-center p-6">
              <div className="relative mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="relative"
                >
                  {companyData.logo && companyData.logo !== "default.jpg" && !companyData.logo.includes("default-logo") ? (
                    <div className="relative">
                      <img
                        src={getImageUrl(companyData.logo)}
                        alt={companyData.name || t("profile.company")}
                        className="h-32 w-32 rounded-full object-cover shadow-2xl ring-4 ring-primary-100 dark:ring-primary-900"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = `
                              <div class="flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 dark:from-primary-900 dark:to-primary-800 dark:text-primary-300 shadow-2xl ring-4 ring-primary-100 dark:ring-primary-900">
                                <BuildingOfficeIcon className="h-16 w-16" />
                              </div>
                            `;
                          }
                        }}
                      />
                      <div className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg">
                        <BuildingOffice2Icon className="h-4 w-4" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 dark:from-primary-900 dark:to-primary-800 dark:text-primary-300 shadow-2xl ring-4 ring-primary-100 dark:ring-primary-900">
                      <BuildingOfficeIcon className="h-16 w-16" />
                    </div>
                  )}
                </motion.div>
              </div>

              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="text-xl font-bold text-gray-900 dark:text-white mb-2"
              >
                {companyData.name || t("profile.unknownCompany")}
              </motion.h3>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="text-gray-600 dark:text-gray-400 mb-4 text-sm"
              >
                {companyData.description || t("profile.noDescription")}
              </motion.p>

              <div className="space-y-2 w-full">
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <IdentificationIcon className="h-4 w-4" />
                  <span>{user?.role || t("profile.unknownRole")}</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <EnvelopeIcon className="h-4 w-4" />
                  <span>{user?.phoneNumber || t("profile.noPhone")}</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <BuildingOffice2Icon className="h-4 w-4" />
                  <span>{companyData.industry || t("profile.unknownIndustry")}</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <CalendarIcon className="h-4 w-4" />
                  <span>
                    {companyData?.createdAt
                      ? formatDate(companyData.createdAt)
                      : t("profile.unknownDate")}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        )}

      </div>
      
      {/* Stats Cards - Individual cards instead of one larger card */}
      <div className={`grid gap-8 ${isCompanyAdmin ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
        {isCompanyAdmin ? (
          <>
            <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
              <div className="p-4 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 1.0 }}
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2"
                >
                  {companyData?.departments?.length || 0}
                </motion.div>
                <div className="text-xs sm:text-sm lg:text-base font-medium text-gray-700 dark:text-gray-300 leading-tight">
                  {t("profile.departments")}
                </div>
              </div>
            </Card>
            
            <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
              <div className="p-4 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 1.1 }}
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2"
                >
                  {companyData?.employees?.length || 0}
                </motion.div>
                <div className="text-xs sm:text-sm lg:text-base font-medium text-gray-700 dark:text-gray-300 leading-tight">
                  {t("profile.employees")}
                </div>
              </div>
            </Card>
          </>
        ) : (
          <div className="grid gap-8 md:grid-cols-3">
            <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
              <div className="p-4 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 1.0 }}
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2"
                >
                  {user?.learningProgress?.length || 0}
                </motion.div>
                  <div className="text-xs sm:text-sm lg:text-base font-medium text-gray-700 dark:text-gray-300 leading-tight">
                    {t("profile.quizTaken")}
                  </div>
              </div>
            </Card>
            
            <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
              <div className="p-4 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 1.1 }}
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2"
                >
                  {user?.certifications?.length || 0}
                </motion.div>
                <div className="text-xs sm:text-sm lg:text-base font-medium text-gray-700 dark:text-gray-300 leading-tight">
                  {t("profile.completions")}
                </div>
              </div>
            </Card>
            
            <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
              <div className="p-4 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 1.2 }}
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2"
                >
                  {user?.badges?.length || 0}
                </motion.div>
                <div className="text-xs sm:text-sm lg:text-base font-medium text-gray-700 dark:text-gray-300 leading-tight">
                  {t("profile.badges")}
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
};

export default ProfileHeader;
